import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../../context/LanguageContext';
import {
    Menu as MenuIcon,
    X,
    ChevronDown,
    ArrowRight,
    Sparkles,
    Users,
    Globe,
    Heart,
    Building,
    HelpCircle,
    FileText,
    Download,
    Shield,
    BookOpen,
    MessageCircle,
    Star,
    Smartphone,
    Tag,
} from 'lucide-react';

// Check if registration is disabled via environment variable
const isRegistrationDisabled = import.meta.env.VITE_DISABLE_REGISTRATION === 'true';

/**
 * Menu Component
 * Navigation menu with mega-menu dropdowns for public pages
 */
const Menu = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeMenu, setActiveMenu] = useState(null);
    const closeTimeoutRef = useRef(null);

    const handleMenuEnter = (menuKey) => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setActiveMenu(menuKey);
    };

    const handleMenuLeave = () => {
        closeTimeoutRef.current = setTimeout(() => {
            setActiveMenu(null);
        }, 150);
    };

    useEffect(() => {
        setActiveMenu(null);
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const megaMenus = {
        discover: {
            title: t.nav?.discover || 'Découvrir',
            sections: [
                {
                    title: t.nav?.features || 'Fonctionnalités',
                    items: [
                        {
                            icon: Users,
                            name: t.nav?.contacts || 'Contacts',
                            description: t.nav?.contactsDesc || 'Gérez vos contacts facilement',
                            href: '/discover',
                            color: 'text-blue-500',
                            bgColor: 'bg-blue-50'
                        },
                        {
                            icon: Globe,
                            name: t.nav?.network || 'Réseau',
                            description: t.nav?.networkDesc || 'Connectez-vous avec d\'autres',
                            href: '/discover',
                            color: 'text-green-500',
                            bgColor: 'bg-green-50'
                        },
                        {
                            icon: Heart,
                            name: t.nav?.services || 'Services',
                            description: t.nav?.servicesDesc || 'Offrez et demandez des services',
                            href: '/discover',
                            color: 'text-pink-500',
                            bgColor: 'bg-pink-50',
                            badge: t.nav?.new || 'Nouveau',
                            badgeColor: 'bg-pink-500'
                        },
                    ],
                },
                {
                    title: t.nav?.getStarted || 'Commencer',
                    items: [
                        {
                            icon: Sparkles,
                            name: t.nav?.pricing || 'Tarifs',
                            description: t.nav?.pricingDesc || 'Découvrez nos formules',
                            href: '/pricing',
                            color: 'text-purple-500',
                            bgColor: 'bg-purple-50'
                        },
                        {
                            icon: Smartphone,
                            name: t.nav?.installApp || 'Installer l\'app',
                            description: t.nav?.installAppDesc || 'Ajoutez Cabaza sur votre téléphone',
                            href: '/install',
                            color: 'text-orange-500',
                            bgColor: 'bg-orange-50',
                            badge: 'PWA',
                            badgeColor: 'bg-orange-500'
                        },
                    ],
                },
            ],
            featured: {
                title: t.nav?.featuredTitle || 'Rejoignez Cabaza',
                description: t.nav?.featuredDesc || 'Le réseau social européen pour échanger services et compétences',
                image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=200&fit=crop',
                link: '/register',
                tag: t.nav?.free || 'Gratuit',
                tagColor: 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink',
            },
        },
        about: {
            title: t.nav?.about || 'À propos',
            sections: [
                {
                    title: t.nav?.company || 'L\'entreprise',
                    items: [
                        {
                            icon: Building,
                            name: t.nav?.ourStory || 'Notre histoire',
                            description: t.nav?.ourStoryDesc || 'Découvrez Cabaza',
                            href: '/discover',
                            color: 'text-slate-500',
                            bgColor: 'bg-slate-50'
                        },
                        {
                            icon: Star,
                            name: t.nav?.testimonials || 'Témoignages',
                            description: t.nav?.testimonialsDesc || 'Ce que disent nos utilisateurs',
                            href: '/discover',
                            color: 'text-yellow-500',
                            bgColor: 'bg-yellow-50'
                        },
                        {
                            icon: Tag,
                            name: t.releases?.releases || 'Versions',
                            description: t.releases?.releasesDesc || 'Consultez l\'historique des versions',
                            href: '/releases',
                            color: 'text-indigo-500',
                            bgColor: 'bg-indigo-50'
                        },
                    ],
                },
                {
                    title: t.nav?.legal || 'Légal',
                    items: [
                        {
                            icon: Shield,
                            name: t.nav?.privacy || 'Confidentialité',
                            description: t.nav?.privacyDesc || 'Protection de vos données',
                            href: '/privacy',
                            color: 'text-emerald-500',
                            bgColor: 'bg-emerald-50'
                        },
                        {
                            icon: FileText,
                            name: t.nav?.terms || 'CGU',
                            description: t.nav?.termsDesc || 'Conditions d\'utilisation',
                            href: '/terms',
                            color: 'text-gray-500',
                            bgColor: 'bg-gray-50'
                        },
                    ],
                },
            ],
            featured: {
                title: t.nav?.installAppTitle || 'Installez l\'application',
                description: t.nav?.installAppFeaturedDesc || 'Accédez à Cabaza directement depuis votre écran d\'accueil',
                image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=200&fit=crop',
                link: '/install',
                tag: 'iOS & Android',
                tagColor: 'bg-gradient-to-r from-orange-500 to-amber-500',
            },
        },
        resources: {
            title: t.nav?.resources || 'Ressources',
            sections: [
                {
                    title: t.nav?.help || 'Aide',
                    items: [
                        {
                            icon: HelpCircle,
                            name: t.nav?.faq || 'FAQ',
                            description: t.nav?.faqDesc || 'Questions fréquentes',
                            href: '/discover',
                            color: 'text-cyan-500',
                            bgColor: 'bg-cyan-50'
                        },
                        {
                            icon: MessageCircle,
                            name: t.nav?.contact || 'Contact',
                            description: t.nav?.contactDesc || 'Contactez-nous',
                            href: '/discover',
                            color: 'text-violet-500',
                            bgColor: 'bg-violet-50'
                        },
                    ],
                },
                {
                    title: t.nav?.guides || 'Guides',
                    items: [
                        {
                            icon: BookOpen,
                            name: t.nav?.documentation || 'Documentation',
                            description: t.nav?.documentationDesc || 'Guides d\'utilisation',
                            href: '/discover',
                            color: 'text-indigo-500',
                            bgColor: 'bg-indigo-50'
                        },
                        {
                            icon: Download,
                            name: t.nav?.installGuide || 'Guide d\'installation',
                            description: t.nav?.installGuideDesc || 'Installez l\'app sur votre appareil',
                            href: '/install',
                            color: 'text-teal-500',
                            bgColor: 'bg-teal-50'
                        },
                    ],
                },
            ],
            featured: null,
        },
    };

    const renderMegaMenu = (menuKey) => {
        const menu = megaMenus[menuKey];
        const hasFeatured = menu.featured && !isRegistrationDisabled;

        return (
            <div
                className={`absolute left-0 right-0 top-full bg-white shadow-2xl border-t border-gray-100 transition-all duration-300 transform origin-top z-50 ${
                    activeMenu === menuKey
                        ? 'opacity-100 scale-y-100 visible'
                        : 'opacity-0 scale-y-95 invisible pointer-events-none'
                }`}
                onMouseEnter={() => handleMenuEnter(menuKey)}
                onMouseLeave={handleMenuLeave}
            >
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className={`grid gap-6 ${hasFeatured ? 'grid-cols-12' : 'grid-cols-8'}`}>
                        {/* Menu sections */}
                        <div className={`${hasFeatured ? 'col-span-8' : 'col-span-8'} grid grid-cols-2 gap-6`}>
                            {menu.sections.map((section, idx) => (
                                <div key={idx}>
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <span className="w-8 h-px bg-gradient-to-r from-cabaza-purple to-transparent"></span>
                                        {section.title}
                                    </h4>
                                    <div className="space-y-1">
                                        {section.items.map((item, itemIdx) => (
                                            <Link
                                                key={itemIdx}
                                                to={item.href}
                                                className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-all group"
                                            >
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${item.bgColor} group-hover:scale-110`}>
                                                    <item.icon className={`w-5 h-5 ${item.color}`} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-gray-900 group-hover:text-cabaza-purple transition-colors">
                                                            {item.name}
                                                        </span>
                                                        {item.badge && (
                                                            <span className={`px-2 py-0.5 text-[10px] font-bold text-white rounded-full uppercase ${item.badgeColor}`}>
                                                                {item.badge}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-500 truncate">{item.description}</div>
                                                </div>
                                                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-cabaza-purple group-hover:translate-x-1 transition-all mt-1 opacity-0 group-hover:opacity-100" />
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Featured card with image */}
                        {hasFeatured && (
                            <div className="col-span-4">
                                <Link
                                    to={menu.featured.link}
                                    className="block relative h-full rounded-2xl overflow-hidden group"
                                >
                                    <img
                                        src={menu.featured.image}
                                        alt=""
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                                    <div className="relative p-5 h-full flex flex-col justify-end min-h-[180px]">
                                        <span className={`inline-block px-3 py-1 text-xs font-bold text-white rounded-full mb-2 w-fit ${menu.featured.tagColor}`}>
                                            {menu.featured.tag}
                                        </span>
                                        <h4 className="text-lg font-bold text-white mb-1">
                                            {menu.featured.title}
                                        </h4>
                                        <p className="text-white/80 text-sm mb-3 line-clamp-2">
                                            {menu.featured.description}
                                        </p>
                                        <span className="inline-flex items-center gap-2 text-white text-sm font-medium group-hover:gap-3 transition-all">
                                            {t.nav?.learnMore || 'En savoir plus'}
                                            <ArrowRight className="w-4 h-4" />
                                        </span>
                                    </div>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1 relative">
                {!isRegistrationDisabled && (
                    <>
                        {/* Discover Menu */}
                        <div
                            className="relative"
                            onMouseEnter={() => handleMenuEnter('discover')}
                            onMouseLeave={handleMenuLeave}
                        >
                            <button
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                    activeMenu === 'discover'
                                        ? 'text-cabaza-purple bg-purple-50'
                                        : 'text-gray-600 hover:text-cabaza-purple hover:bg-purple-50'
                                }`}
                            >
                                {t.nav?.discover || 'Découvrir'}
                                <ChevronDown className={`w-4 h-4 transition-transform ${activeMenu === 'discover' ? 'rotate-180' : ''}`} />
                            </button>
                        </div>

                        {/* About Menu */}
                        <div
                            className="relative"
                            onMouseEnter={() => handleMenuEnter('about')}
                            onMouseLeave={handleMenuLeave}
                        >
                            <button
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                    activeMenu === 'about'
                                        ? 'text-cabaza-purple bg-purple-50'
                                        : 'text-gray-600 hover:text-cabaza-purple hover:bg-purple-50'
                                }`}
                            >
                                {t.nav?.about || 'À propos'}
                                <ChevronDown className={`w-4 h-4 transition-transform ${activeMenu === 'about' ? 'rotate-180' : ''}`} />
                            </button>
                        </div>

                        {/* Resources Menu */}
                        <div
                            className="relative"
                            onMouseEnter={() => handleMenuEnter('resources')}
                            onMouseLeave={handleMenuLeave}
                        >
                            <button
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                    activeMenu === 'resources'
                                        ? 'text-cabaza-purple bg-purple-50'
                                        : 'text-gray-600 hover:text-cabaza-purple hover:bg-purple-50'
                                }`}
                            >
                                {t.nav?.resources || 'Ressources'}
                                <ChevronDown className={`w-4 h-4 transition-transform ${activeMenu === 'resources' ? 'rotate-180' : ''}`} />
                            </button>
                        </div>

                        {/* Pricing link */}
                        <Link
                            to="/pricing"
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                location.pathname === '/pricing'
                                    ? 'text-cabaza-purple'
                                    : 'text-gray-600 hover:text-cabaza-purple hover:bg-purple-50'
                            }`}
                        >
                            {t.nav?.pricing || 'Tarifs'}
                        </Link>
                    </>
                )}

                {/* Login link - always visible */}
                <Link
                    to="/login"
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white hover:shadow-lg hover:scale-[1.02] transition-all"
                >
                    {t.nav?.login || 'Se connecter'}
                </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 min-w-11 min-h-11 sm:min-w-0 sm:min-h-0 inline-flex items-center justify-center rounded-lg text-gray-700 hover:text-cabaza-purple hover:bg-purple-50 transition-colors"
            >
                {mobileMenuOpen ? (
                    <X className="w-6 h-6" />
                ) : (
                    <MenuIcon className="w-6 h-6" />
                )}
            </button>

            {/* Mobile Navigation */}
            <div
                className={`md:hidden fixed inset-x-0 top-20 bottom-0 bg-white z-50 transition-all duration-300 overflow-y-auto ${
                    mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
                }`}
            >
                <div className="p-4 space-y-2">
                    {!isRegistrationDisabled && (
                        <>
                            {/* Mobile Discover */}
                            <div className="px-4 py-2">
                                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                    {t.nav?.discover || 'Découvrir'}
                                </div>
                                {megaMenus.discover.sections.flatMap(s => s.items).map((item, idx) => (
                                    <Link
                                        key={idx}
                                        to={item.href}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="flex items-center gap-3 px-2 py-2 rounded-lg text-gray-600 hover:text-cabaza-purple hover:bg-purple-50 transition-colors"
                                    >
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.bgColor}`}>
                                            <item.icon className={`w-4 h-4 ${item.color}`} />
                                        </div>
                                        <span className="font-medium">{item.name}</span>
                                        {item.badge && (
                                            <span className={`px-1.5 py-0.5 text-[10px] font-bold text-white rounded-full ${item.badgeColor}`}>
                                                {item.badge}
                                            </span>
                                        )}
                                    </Link>
                                ))}
                            </div>

                            {/* Mobile About */}
                            <div className="px-4 py-2">
                                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                    {t.nav?.about || 'À propos'}
                                </div>
                                {megaMenus.about.sections.flatMap(s => s.items).map((item, idx) => (
                                    <Link
                                        key={idx}
                                        to={item.href}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="flex items-center gap-3 px-2 py-2 rounded-lg text-gray-600 hover:text-cabaza-purple hover:bg-purple-50 transition-colors"
                                    >
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.bgColor}`}>
                                            <item.icon className={`w-4 h-4 ${item.color}`} />
                                        </div>
                                        <span className="font-medium">{item.name}</span>
                                    </Link>
                                ))}
                            </div>

                            {/* Mobile Resources */}
                            <div className="px-4 py-2">
                                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                                    {t.nav?.resources || 'Ressources'}
                                </div>
                                {megaMenus.resources.sections.flatMap(s => s.items).map((item, idx) => (
                                    <Link
                                        key={idx}
                                        to={item.href}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="flex items-center gap-3 px-2 py-2 rounded-lg text-gray-600 hover:text-cabaza-purple hover:bg-purple-50 transition-colors"
                                    >
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.bgColor}`}>
                                            <item.icon className={`w-4 h-4 ${item.color}`} />
                                        </div>
                                        <span className="font-medium">{item.name}</span>
                                    </Link>
                                ))}
                            </div>

                            <Link
                                to="/pricing"
                                onClick={() => setMobileMenuOpen(false)}
                                className="block px-4 py-2.5 rounded-xl text-gray-700 hover:text-cabaza-purple hover:bg-purple-50 font-medium transition-colors"
                            >
                                {t.nav?.pricing || 'Tarifs'}
                            </Link>
                        </>
                    )}

                    <div className="pt-4">
                        <Link
                            to="/login"
                            onClick={() => setMobileMenuOpen(false)}
                            className="block text-center bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
                        >
                            {t.nav?.login || 'Se connecter'}
                        </Link>
                    </div>
                </div>
            </div>

            {/* Mega Menus (rendered outside nav for proper positioning) */}
            {!isRegistrationDisabled && (
                <div className="hidden md:block">
                    {renderMegaMenu('discover')}
                    {renderMegaMenu('about')}
                    {renderMegaMenu('resources')}
                </div>
            )}
        </>
    );
};

export default Menu;
